/**
 * Owns the one question MAHI asks before showing anybody a card.
 *
 * Everything else the app wants to know — a name, a height, a photo, a phone
 * number — is now deferred until the feature that needs it. This is the single
 * exception, and it earns the exception on the numbers: the live catalogue is
 * 45,613 womenswear products against 5,380 menswear, so a menswear shopper who
 * is never asked spends their entire first session passing on dresses. Every
 * other unanswered question costs a little relevance; this one costs the whole
 * feed.
 *
 * Shaped deliberately like useDiscoveryPrefs and useDiscoverTour, because the
 * three of them are the same kind of decision (a first-run surface gated on a
 * server-side flag) and the feed sequences them in that order. The flag lives
 * on the account (`shopping_for`) rather than in localStorage for the reason
 * the other two do: browser storage would re-ask on a second device and after
 * any cleared browser.
 *
 * Guests answer it the same way registered users do, and their answer persists
 * the same way, because a guest has a real account row. That is the entire
 * argument for guest mode in one field.
 */
import { useCallback, useEffect, useState } from 'react';
import { useMahiAuth } from '../../auth';
import { resetDiscoverFeed } from '../../prefetch';

export default function useShoppingFor({ user, authLoading, ready }) {
  const { refresh } = useMahiAuth();
  const [active, setActive] = useState(false);
  // Latches once, so a `refresh()` landing while the sheet is open — or right
  // after it closes, before the new field has propagated — cannot reopen it.
  const [settled, setSettled] = useState(false);

  useEffect(() => {
    if (settled || authLoading || !ready || !user) return;
    // A registered account that answered `gender` at onboarding has already
    // told us enough; re-asking would be a question whose answer we hold.
    if (user.shopping_for || user.gender) { setSettled(true); return; }
    setActive(true);
    setSettled(true);
  }, [settled, authLoading, ready, user]);

  /**
   * Answered. Unlike the preference picker there is no "skip" path here — the
   * three choices (womenswear, menswear, everything) already include the one
   * that means "don't filter me", so a fourth way to decline would just be an
   * unlabelled duplicate of the third.
   */
  const finish = useCallback(() => {
    setActive(false);
    // Any deck cached before the answer was built for the wrong half of the
    // catalogue. Dropping it here is what makes the very next screen reflect
    // the tap, which is the whole reason this question is asked in front of
    // the feed rather than inside a settings page.
    resetDiscoverFeed();
    refresh();
  }, [refresh]);

  return {
    shoppingForActive: active,
    // What the surfaces after this one wait on — true once the question has
    // been decided one way or another, including "they already had an answer".
    shoppingForSettled: settled,
    finishShoppingFor: finish,
  };
}
